import type { Interval } from "../time";
import type { Company, Department, Employee, Position } from "../model";
import type { PentacodeAPIModels } from "../rest/api";

export function displayDistinctWorkarea(company: Company, employee: Employee, position: Position) {
    const { venue, department } = company.getDepartment(position.departmentId);
    if (!venue || !department) {
        return position.name;
    }

    const isPositionNameUnique = !employee.positions.some(
        (p) => p.id !== position.id && p.name.trim() === position.name.trim()
    );

    if (isPositionNameUnique) {
        return position.name;
    }

    const isDepartmentNameUnique = !employee.positions.some(
        (pos) =>
            pos.id !== position.id &&
            company.getDepartment(pos.departmentId).department?.name.trim() === department.name.trim()
    );

    if (isDepartmentNameUnique || company.venues.length < 2) {
        return `${department.name} / ${position.name}`;
    }

    return `${venue.name} / ${department.name} / ${position.name}`;
}

export function hasConflictingEmployee(
    company: Company,
    employee: Pick<PentacodeAPIModels["Employee"], "email" | "timeLogPin" | "staffNumber">
) {
    const { email, timeLogPin, staffNumber } = employee;
    if (email || timeLogPin || staffNumber) {
        return company.employees.some(
            (emp) =>
                (email && email === emp.email) ||
                (staffNumber && Number(staffNumber) === emp.staffNumber) ||
                (timeLogPin && timeLogPin === emp.timePin)
        );
    }
    return false;
}

export function getEmployeeOrderInDepartment(emp: Employee, dep: Department) {
    const departmentPositions = emp.positions.filter((p) => p.departmentId === dep.id);
    const posOrder = departmentPositions.length > 0 ? Math.min(...departmentPositions.map((p) => p.order)) : 0;
    const order = dep.rosterOrder.indexOf(emp.id.toString());
    return order === -1 ? dep.rosterOrder.length + posOrder : order;
}

export function isAdult(dateOfBirth: Date, now: Date) {
    const eighteenYearsAgo = new Date(now.getFullYear() - 18, now.getMonth(), now.getDate());

    return dateOfBirth <= eighteenYearsAgo;
}

export function formatEmployedInterval(interval: Interval | null): string {
    if (!interval) return "";

    const [from, to] = interval;
    const toCopy = new Date(to);
    toCopy.setDate(toCopy.getDate() + 1); // plus one day to account for to day to be counted as well

    let years = toCopy.getFullYear() - from.getFullYear();
    let months = toCopy.getMonth() - from.getMonth();
    let days = toCopy.getDate() - from.getDate();

    if (days < 0) {
        months--;
        days += new Date(toCopy.getFullYear(), toCopy.getMonth(), 0).getDate();
    }

    if (months < 0) {
        years--;
        months += 12;
    }

    if (years < 0) return "";

    const parts = [
        years > 0 ? `${years} ${years === 1 ? "Jahr" : "Jahre"}` : "",
        months > 0 ? `${months} ${months === 1 ? "Monat" : "Monate"}` : "",
        days > 0 && years === 0 ? `${days} ${days === 1 ? "Tag" : "Tage"}` : "",
    ];

    return parts.filter(Boolean).join(", ");
}

export function findEmployeeById<T extends { id: number }>(employees: T[], id: number) {
    return employees && employees.find((e) => e.id === id);
}

export function findEmployeeByStaffNumber<T extends { staffNumber: number }>(employees: T[], staffNumber: number) {
    return employees && employees.find((e) => e.staffNumber === staffNumber);
}
